import {
  Box,
  Grid,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ReportTable from "components/report/ReportTable/ReportTable";
import { drawerWidth } from "constants/common";
import { ChartType } from "enums/report";
import { ThemeMode } from "enums/theme";
import {
  AxisMapped,
  FieldType,
  ReportDto,
  SubmitReportDto,
  YAxisID,
} from "interfaces/report";
import { cloneDeep, get, max, pick } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { reportService } from "services/reportService";
import { RootState } from "store/config";
import { mergeArrays } from "utils/array";
import { convertStringToNumber, isTotalKey } from "utils/string";
import ReportTabSheets from "../ReportTabSheets/ReportTabSheets";
import HeaderAction from "./components/HeaderAction";
import ReportAttachments from "./components/ReportAttachments";
import ReportChart from "./components/ReportChart";
import ReportCommentary from "./components/ReportCommentary";
import SharedEmailsDialog from "./components/SharedEmailsDialog";

interface Props {
  reportDto: ReportDto;
  revisionMode?: boolean;
}

export default function ReportDetailContent(props: Props) {
  const { revisionMode = false, reportDto } = props || {};
  const params = useParams();
  const navigate = useNavigate();
  const userState = useSelector((state: RootState) => state.userReducer);
  const [report, setReport] = useState<ReportDto<AxisMapped>>(reportDto);
  const [isInitialized, setIsInitialized] = useState<boolean>();
  const [chartType, setChartType] = useState<ChartType>();
  const [commentary, setCommentary] = useState<string>();
  const [sheetName, setSheetName] = useState<string>();
  const [openSharedEmailsDialog, setOpenSharedEmailsDialog] =
    useState<boolean>(false);
  const theme = useTheme();
  const matchDownLG = useMediaQuery(theme.breakpoints.down("lg"));
  const { drawerOpen } = useSelector((state: RootState) => state.menuReducer);

  const isShowRevision = useMemo(() => {
    return !revisionMode && !!report && report.createdAt !== report.updatedAt;
  }, [report, revisionMode]);

  const isOwner = useMemo(() => {
    return (
      !revisionMode &&
      !!report &&
      (report.createdBy as string) === userState.currentUser?._id
    );
  }, [report, revisionMode]);

  const useCircleChart = useMemo(() => {
    return !!(
      chartType && [ChartType.DOUGHNUT, ChartType.PIE].includes(chartType)
    );
  }, [chartType]);

  useEffect(() => {
    if (!isInitialized) {
      const sheetName = Object.keys(report.data)[0];

      setSheetName(sheetName);
      setChartType(report.data[sheetName].chartType);
      setCommentary(report.data[sheetName].commentary);
      setReport(mappingReport(report));
      setIsInitialized(true);
    }
  }, [isInitialized]);

  const mappingReport = (report: ReportDto) => {
    const mappedReport = report as ReportDto<AxisMapped>;

    for (const key in mappedReport.data) {
      let isActivated: boolean = false;
      const sheet = mappedReport.data[key];

      // @ts-ignore
      sheet.data = sheet.data.map((row) => {
        return row.map((cell) => {
          return convertStringToNumber(cell?.toString() || "", true);
        });
      });

      sheet.xAxis = sheet.xAxis.map((item) => {
        const active = item.active === undefined ? true : item.active;
        return { ...item, hide: isTotalKey(item.value), active };
      });

      mappedReport.data[key] = sheet;
    }
    return mappedReport;
  };

  const handleChangeColor = async (
    key: string,
    color: string,
    axis: "xAxis" | "yAxis"
  ) => {
    const clonedReport = cloneDeep(report) as ReportDto;

    clonedReport.data[sheetName as string][axis] = clonedReport.data[
      sheetName as string
    ][axis].map((item) => {
      if (item.value === key) {
        item.color = color;
      }

      return item;
    });

    const submitReportDto = pick(
      mappingRemoveHideAndActiveReport(clonedReport),
      ["name", "data"]
    ) as SubmitReportDto;

    setReport(clonedReport);
    await reportService.update(params.id as string, submitReportDto);
  };

  const mappingRemoveHideAndActiveReport = (report: ReportDto<AxisMapped>) => {
    return cloneDeep(report);
  };

  const handleSelectRow = async (axisKey: string) => {
    if (!report || !sheetName) return;

    const cloneReport = cloneDeep(report);

    cloneReport.data[sheetName].yAxis = cloneReport.data[sheetName].yAxis.map(
      (item) =>
        item.value === axisKey ? { ...item, highlight: !item.highlight } : item
    );

    const submitReportDto = pick(
      mappingRemoveHideAndActiveReport(cloneReport),
      ["name", "data"]
    ) as SubmitReportDto;
    setReport(cloneReport);
    await reportService.update(params.id as string, submitReportDto);
  };

  const handleChangeAxisLabels = async (
    axisKey: string,
    axis: "xAxis" | "yAxis"
  ) => {
    if (!report || !sheetName) return;
    const cloneReport = cloneDeep(report);

    cloneReport.data[sheetName][axis] = cloneReport.data[sheetName][axis].map(
      (item) => {
        const isOneSelect =
          useCircleChart || chartType === ChartType.STACKED_BAR_GROUP;

        if (isOneSelect && axis === "yAxis") {
          item.active = item.value === axisKey;
          return item;
        }

        return item.value === axisKey
          ? {
              ...item,
              active: !item.active,
              yAxisID: item.active ? undefined : YAxisID.Y,
              fieldType: item.active ? undefined : FieldType.BAR,
            }
          : item;
      }
    );

    const submitReportDto = pick(
      mappingRemoveHideAndActiveReport(cloneReport),
      ["name", "data"]
    ) as SubmitReportDto;
    setReport(cloneReport);
    await reportService.update(params.id as string, submitReportDto);
  };

  const handleChangeChartType = async (chartType: ChartType) => {
    const clonedReport = cloneDeep(report) as ReportDto;

    clonedReport.data[sheetName as string].chartType = chartType;

    const submitReportDto = pick(
      mappingRemoveHideAndActiveReport(clonedReport),
      ["name", "data"]
    ) as SubmitReportDto;

    setChartType(chartType);
    setReport(mappingReport(clonedReport));
    await reportService.update(params.id as string, submitReportDto);
  };

  const handleChangeSheetName = (sheetName: string) => {
    setChartType(report?.data[sheetName].chartType);
    setCommentary(report?.data[sheetName].commentary);
    setSheetName(sheetName);
  };

  useEffect(() => {
    const wrapperPageElement = document.querySelector(".wrapper-page") as any;

    wrapperPageElement.style.marginBottom = `50px`;
    wrapperPageElement.style.borderBottomLeftRadius = `0`;
    wrapperPageElement.style.borderBottomRightRadius = `0`;
  }, []);

  const onChangeAxisUnit = async (id: YAxisID, unit: string = "") => {
    if (!report || !sheetName) return;
    const cloneReport = cloneDeep(report);
    // Just find a left axis, then assign unit is enough
    const axis = cloneReport.data[sheetName].yAxis.find((axis) => {
      return axis.yAxisID === id;
    });

    if (axis) {
      axis.unit = unit;

      const submitReportDto = pick(
        mappingRemoveHideAndActiveReport(cloneReport),
        ["name", "data"]
      ) as SubmitReportDto;

      setReport(cloneReport);
      await reportService.update(params.id as string, submitReportDto);
    }
  };

  const handleChangeYAxisID = async (keys: string[], id: YAxisID) => {
    if (!report || !sheetName) return;
    const cloneReport = cloneDeep(report);

    cloneReport.data[sheetName].yAxis = cloneReport.data[sheetName].yAxis.map(
      (item) => {
        if (item.yAxisID === id) {
          item.yAxisID = undefined;
          item.active = false;
        }

        for (const key of keys) {
          if (item.value === key) {
            item.yAxisID = id;
            item.active = true;
          }
        }

        return item;
      }
    );

    const submitReportDto = pick(
      mappingRemoveHideAndActiveReport(cloneReport),
      ["name", "data"]
    ) as SubmitReportDto;

    setReport(cloneReport);
    await reportService.update(params.id as string, submitReportDto);
  };

  const handleChangeXLabel = async (key: string) => {
    if (!report || !sheetName) return;

    const clonedReport = cloneDeep(report);

    clonedReport.data[sheetName].yAxis = clonedReport.data[sheetName].yAxis.map(
      (item) => {
        item.isFieldGroup = item.value === key;

        return item;
      }
    );

    const submitReportDto = pick(
      mappingRemoveHideAndActiveReport(clonedReport),
      ["name", "data"]
    ) as SubmitReportDto;

    setReport(clonedReport);
    await reportService.update(params.id as string, submitReportDto);
  };

  const handleChangeXAxis = async (key: string) => {
    if (!report || !sheetName) return;

    const clonedReport = cloneDeep(report);

    const maxSheetLength =
      (max(
        clonedReport.data[sheetName].data.map((items) => items.length)
      ) as number) - 1;

    const data = clonedReport.data[sheetName].data
      .find((items) => items[0] === key)
      ?.slice(1);

    const mappedData = mergeArrays(
      Array(maxSheetLength).fill(null),
      data || []
    );

    clonedReport.data[sheetName].xAxis = clonedReport.data[sheetName].xAxis.map(
      (item, idx) => {
        item.value = mappedData[idx] || `Unknown Name ${idx + 1}`;

        return item;
      }
    );

    clonedReport.data[sheetName].yAxis = clonedReport.data[sheetName].yAxis.map(
      (item) => {
        item.isXAxis = item.value === key;

        return item;
      }
    );

    const submitReportDto = pick(
      mappingRemoveHideAndActiveReport(clonedReport),
      ["name", "data"]
    ) as SubmitReportDto;

    setReport(clonedReport);
    await reportService.update(params.id as string, submitReportDto);
  };

  const handleChangeBarLineField = async (
    keys: string[],
    fieldType: FieldType
  ) => {
    if (!report || !sheetName) return;

    const cloneReport = cloneDeep(report);

    cloneReport.data[sheetName].yAxis = cloneReport.data[sheetName].yAxis.map(
      (item) => {
        if (item.fieldType === fieldType) {
          item.fieldType = undefined;
          item.active = false;
        }

        for (const key of keys) {
          if (item.value === key) {
            item.fieldType = fieldType;
            item.active = true;
          }
        }

        item.yAxisID = YAxisID.Y1;

        return item;
      }
    );

    const submitReportDto = pick(
      mappingRemoveHideAndActiveReport(cloneReport),
      ["name", "data"]
    ) as SubmitReportDto;

    setReport(cloneReport);
    await reportService.update(params.id as string, submitReportDto);
  };

  return (
    <Grid container spacing={3} style={{ position: "relative" }}>
      <Grid item xs={12}>
        <HeaderAction
          revisionMode={revisionMode}
          isOwner={isOwner}
          setOpenSharedEmailsDialog={setOpenSharedEmailsDialog}
          report={cloneDeep(report)}
        />
      </Grid>

      {isShowRevision && (
        <Grid item xs={12}>
          <Stack direction="row" spacing={2}>
            <Typography>
              Last updated on{" "}
              {moment(report.updatedAt).format("MMM D, YYYY HH:mm:ss")}
            </Typography>

            <Link
              underline="always"
              component="button"
              onClick={() => navigate(`/reports/${report._id}/revisions`)}
            >
              View all revisions
            </Link>
          </Stack>
        </Grid>
      )}

      {sheetName && report.data && (
        <Box
          style={{
            position: "fixed",
            left: `calc(${
              !matchDownLG && drawerOpen ? drawerWidth : "-1px"
            } + 16px)`,
            right: 15,
            bottom: 0,
            backgroundColor: "white",
            zIndex: 1,
            borderTop: `1px solid ${
              theme.palette.mode === ThemeMode.LIGHT ? "#e5e5e5" : "#ffffff0d"
            }`,
            background: get(theme, "palette.grey.A50"),
            boxShadow:
              "rgba(60, 64, 67, 0.11) 0px 1px 2px 0px, rgba(28, 81, 121, 0.01) 0px 1px 3px 1px",
          }}
        >
          <ReportTabSheets
            sheetName={sheetName}
            reportData={cloneDeep(report.data)}
            handleChangeSheetName={handleChangeSheetName}
          />
        </Box>
      )}

      {report.data && sheetName && (
        <Grid item xs={12}>
          <ReportChart
            handleChangeChartType={handleChangeChartType}
            useCircleChart={useCircleChart}
            reportData={cloneDeep(report.data)}
            chartType={chartType}
            isOwner={isOwner}
            sheetName={sheetName}
            handleChangeAxisLabels={handleChangeAxisLabels}
            handleChangeColor={handleChangeColor}
            handleChangeYAxisID={handleChangeYAxisID}
            handleChangeXAxis={handleChangeXAxis}
            handleChangeXLabel={handleChangeXLabel}
            handleChangeBarLineField={handleChangeBarLineField}
            onChangeAxisUnit={onChangeAxisUnit}
          />
        </Grid>
      )}

      {sheetName && report.data && (
        <Grid item xs={12}>
          <ReportTable
            isOwner={isOwner}
            chartType={chartType}
            reportData={cloneDeep(report.data)}
            sheetName={sheetName}
            handleChangeSheetName={handleChangeSheetName}
            handleChangeChartType={handleChangeChartType}
            handleSelectRow={handleSelectRow}
          />
        </Grid>
      )}

      {commentary && (
        <Grid item xs={12}>
          <ReportCommentary commentary={commentary} />
        </Grid>
      )}

      {report.attachments && (
        <Grid item xs={12}>
          <ReportAttachments attachments={report.attachments} />
        </Grid>
      )}

      <SharedEmailsDialog
        handleClose={() => setOpenSharedEmailsDialog(false)}
        open={openSharedEmailsDialog}
        report={report}
      />
    </Grid>
  );
}
